import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export function tooltipPosition(rect: Pick<DOMRect, "left" | "top" | "bottom">,
  size: { width: number; height: number }, viewport: { width: number; height: number }) {
  const margin = 8;
  const gap = 6;
  const left = Math.max(margin, Math.min(rect.left, viewport.width - size.width - margin));
  const below = rect.bottom + gap;
  const above = rect.top - size.height - gap;
  const top = below + size.height <= viewport.height - margin || above < margin
    ? Math.max(margin, Math.min(below, viewport.height - size.height - margin))
    : above;
  return { left, top };
}

export function Week39InfoTooltip({ id, label, text, symbol = "i", textTrigger = false }: {
  id: string; label: string; text: string; symbol?: string; textTrigger?: boolean;
}) {
  const triggerRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enter = () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); setDismissed(false); setHovered(true); };
  const leave = () => { leaveTimer.current = setTimeout(() => setHovered(false), 120); };
  useEffect(() => () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); }, []);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const visible = (!textTrigger || overflowing) && !dismissed && (hovered || focused || pinned);
  useLayoutEffect(() => {
    if (!textTrigger || !triggerRef.current) return;
    const element = triggerRef.current;
    const measure = () => setOverflowing(element.scrollHeight > element.clientHeight + 1 || element.scrollWidth > element.clientWidth + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text, textTrigger]);
  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    const tooltip = tooltipRef.current;
    if (!rect || !tooltip) return;
    setPosition(tooltipPosition(rect, { width: tooltip.offsetWidth, height: tooltip.offsetHeight },
      { width: window.innerWidth, height: window.innerHeight }));
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    place();
  }, [visible, text, place]);

  useEffect(() => {
    if (!visible) return;
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [visible, place]);

  useEffect(() => {
    if (!pinned) return;
    const dismiss = (event: PointerEvent) => {
      if (!triggerRef.current?.contains(event.target as Node) && !tooltipRef.current?.contains(event.target as Node)) { setPinned(false); setDismissed(true); }
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [pinned]);

  const toggle = () => { if (pinned) { setPinned(false); setDismissed(true); } else { setDismissed(false); setPinned(true); } };
  const interactions = {
    "aria-describedby": visible ? id : undefined,
    onMouseEnter: enter, onMouseLeave: leave,
    onFocus: () => { setDismissed(false); setFocused(true); },
    onBlur: () => { setFocused(false); setPinned(false); },
  };
  const dismiss = () => { setPinned(false); setHovered(false); setFocused(false); setDismissed(true); };
  return <>
    {textTrigger ? <span ref={(element) => { triggerRef.current = element; }} className="pack39-product-name"
      role={overflowing ? "button" : undefined} tabIndex={overflowing ? 0 : undefined} aria-label={overflowing ? label : undefined}
      data-overflowing={overflowing || undefined} {...interactions} onClick={() => { if (overflowing) toggle(); }}
      onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); dismiss(); }
        else if (overflowing && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); toggle(); } }}>{text}</span>
      : <button ref={(element) => { triggerRef.current = element; }} className="pack39-excel-trigger" type="button" aria-label={label}
        {...interactions} onClick={toggle} onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); dismiss(); } }}>{symbol}</button>}
    {visible ? createPortal(<span ref={tooltipRef} id={id} className="pack39-excel-tooltip" role="tooltip"
      onMouseEnter={enter} onMouseLeave={leave} style={position ?? { visibility: "hidden" }}>{text}</span>, document.body) : null}
  </>;
}
